import React from 'react';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';
import dayjs from 'dayjs';

import sumLists from '../sumLists';
import parsePagesCrawledRawData from './parsePagesCrawledRawData';
import { ChartCard } from '~components/Charts';
import ChartCardDoubleLayout from '../components/ChartCardDoubleLayout';
import PagesCrawledMainChart, { MAIN_CHART_ID } from './PagesCrawledMainChart';
import PagesCrawledSideChart, { SIDE_CHART_ID } from './PagesCrawledSideChart';
import PagesCrawledMainChartLegend from './PagesCrawledMainChartLegend';
import { describeDateRange } from '~components/Filters';
import { useChartItemsVisibility, type ChartConfig } from '~library/chartjs';
import useTrackDashboardV3Event from '../useTrackDashboardV3Event';
import { type WidgetLoadError } from '~features/empty-states';
import ChartLoadError from '../components/ChartLoadError';

export const PAGES_CRAWLED_VISIBILITY_CHARTS: ChartConfig[] = [
  { id: MAIN_CHART_ID, type: 'dataset' },
  { id: SIDE_CHART_ID, type: 'data' },
];

const MAX_CRAWLERS_DISPLAYABLE = 6;
export const TOOLTIP_MESSAGE =
  'The number of pages visited by crawlers over your selected period. A steady or rising trend means good visibility, while consistent drops may indicate visibility issues. Tracking this helps ensure that crawlers are consistently discovering your content.';

export type ChartPagesCrawledProps = {
  rawData?: AggregatedMetrics.PagesCrawledResult;
  isLoading: boolean;
  error?: WidgetLoadError;
  dateRange: [Date, Date];
  descriptionVisible?: boolean;
};

function ChartPagesCrawledCard({
  rawData,
  isLoading,
  error,
  dateRange,
  descriptionVisible = false,
}: ChartPagesCrawledProps) {
  const activeFilterLabel = React.useMemo(() => describeDateRange(dayjs(dateRange[0]), dayjs(dateRange[1])), [dateRange]);

  return (
    <ChartCard
      isLoading={isLoading}
      errorFallback={error && <ChartLoadError onRetry={error.onRetry} />}
      title={`Pages Crawled (${activeFilterLabel})`}
      description={TOOLTIP_MESSAGE}
      descriptionType={descriptionVisible ? 'paragraph' : 'tooltip'}
    >
      {rawData && <ChartPagesCrawled rawData={rawData} />}
    </ChartCard>
  );
}

function ChartPagesCrawled({ rawData }: { rawData: AggregatedMetrics.PagesCrawledResult }) {
  const track = useTrackDashboardV3Event();
  const ChartItemsVisibility = useChartItemsVisibility(PAGES_CRAWLED_VISIBILITY_CHARTS);

  const data = React.useMemo(() => parsePagesCrawledRawData(rawData), [rawData]);
  const totalRequestPerCrawler = React.useMemo(() => {
    const displayableTotalRequests = data.totalRequestPerCrawlerList.slice(0, MAX_CRAWLERS_DISPLAYABLE - 1);

    if (data.totalRequestPerCrawlerList.length > MAX_CRAWLERS_DISPLAYABLE - 1) {
      const otherTotalRequests = data.totalRequestPerCrawlerList
        .slice(MAX_CRAWLERS_DISPLAYABLE - 1)
        .reduce((acc, [_, amount]) => {
          acc += amount;
          return acc;
        }, 0);

      displayableTotalRequests.push(['grouped', otherTotalRequests]);
    }
    return displayableTotalRequests;
  }, [data.totalRequestPerCrawlerList]);

  const crawlersData = React.useMemo(() => {
    const displayableCrawlers = data.crawlersValuesList.slice(0, MAX_CRAWLERS_DISPLAYABLE - 1);

    if (data.crawlersValuesList.length > MAX_CRAWLERS_DISPLAYABLE - 1) {
      const otherCrawlersData = sumLists(
        data.crawlersValuesList.slice(MAX_CRAWLERS_DISPLAYABLE - 1).map(([_, data]) => data)
      );
      displayableCrawlers.push(['grouped', otherCrawlersData]);
    }

    return displayableCrawlers;
  }, [data.crawlersValuesList]);

  const onLegendItemClicked = (datasetIndex: number) => {
    track('ChartDatasetToggled', {
      chart: 'pages crawled',
      dataset: datasetIndex < crawlersData.length ? crawlersData[datasetIndex][0] : 'uncached',
      action: ChartItemsVisibility.itemsVisibility[datasetIndex] ? 'hide' : 'show',
    });
    ChartItemsVisibility.toggleVisibility(datasetIndex);
  };

  React.useEffect(() => {
    ChartItemsVisibility.restoreVisibility(crawlersData.length + 1); // extra one for uncached values
  }, [crawlersData]);

  return (
    <ChartCardDoubleLayout
      MainChart={
        <ChartCard.Content>
          <PagesCrawledMainChartLegend
            totalRequestPerCrawler={totalRequestPerCrawler}
            totalUncachedPercentage={data.totalUncachedPercentage}
            onItemClicked={onLegendItemClicked}
            itemsVisibility={ChartItemsVisibility.itemsVisibility}
          />
          <ChartCard.ChartLayout>
            <PagesCrawledMainChart
              intervals={rawData.intervals}
              crawlersData={crawlersData}
              uncachedData={data.uncachedValuesList}
              granularity={rawData.granularity}
            />
          </ChartCard.ChartLayout>
        </ChartCard.Content>
      }
      SideChart={
        <PagesCrawledSideChart
          onItemClicked={onLegendItemClicked}
          itemsVisibility={ChartItemsVisibility.itemsVisibility}
          totalRequestPerCrawler={totalRequestPerCrawler}
        />
      }
    />
  );
}

export default ChartPagesCrawledCard;
